import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  CreateTaskAssignmentRuleRequest,
  UpdateTaskAssignmentRuleRequest,
  ReorderAssignmentRulesRequest,
} from '@vagusx/clients';
import { getTaskAssignmentRuleClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';

/** Priority-ordered regular rules (excludes the catch-all). */
export function useAssignmentRules() {
  return useQuery({
    queryKey: queryKeys.assignmentRules.list(),
    queryFn: () => getTaskAssignmentRuleClient().list(),
  });
}

/**
 * The org's catch-all rule (zero or one). Returns `{ rule: T | null }`;
 * the caller reads `data?.rule` directly — no list-shape wrapping.
 * Separate cache key so invalidation is targeted.
 */
export function useCatchAllAssignmentRule() {
  return useQuery({
    queryKey: queryKeys.assignmentRules.catchAll(),
    queryFn: () => getTaskAssignmentRuleClient().getCatchAll(),
  });
}

export function useAssignmentRule(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.assignmentRules.detail(id ?? ''),
    queryFn: () => getTaskAssignmentRuleClient().getById(id!),
    enabled: !!id,
  });
}

export function useCreateAssignmentRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTaskAssignmentRuleRequest) =>
      getTaskAssignmentRuleClient().create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assignmentRules.all });
      toast.success('Assignment rule created');
    },
  });
}

export function useUpdateAssignmentRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTaskAssignmentRuleRequest }) =>
      getTaskAssignmentRuleClient().update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assignmentRules.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.assignmentRules.detail(id) });
      toast.success('Assignment rule updated');
    },
  });
}

export function useDeleteAssignmentRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getTaskAssignmentRuleClient().deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assignmentRules.all });
      toast.success('Assignment rule deleted');
    },
  });
}

export function useReorderAssignmentRules() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReorderAssignmentRulesRequest) =>
      getTaskAssignmentRuleClient().reorder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assignmentRules.all });
    },
  });
}
